// The branch against its base, as a person reviews it: which files, how
// much, and each file's patch on a click. From /workspaces/:id/diff.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react'
import type { Diff } from '@one-deployment/api'
import { ApiError, enc, get } from '@/lib/api'
import { cn } from '@/lib/cn'

export type FileDiff = { path: string; status: 'added' | 'deleted' | 'renamed' | 'changed'; from?: string; adds: number; dels: number; lines: string[] }

/** A unified diff split per file, with its counts. Binary files have no lines. */
export function parseDiff(patch: string): FileDiff[] {
  const files: FileDiff[] = []
  let f: FileDiff | null = null
  for (const line of patch.split('\n')) {
    const head = line.match(/^diff --git a\/(.+?) b\/(.+)$/)
    if (head) {
      f = { path: head[2]!, status: 'changed', adds: 0, dels: 0, lines: [] }
      if (head[1] !== head[2]) Object.assign(f, { status: 'renamed', from: head[1] })
      files.push(f)
      continue
    }
    if (!f) continue
    if (line.startsWith('new file mode')) f.status = 'added'
    else if (line.startsWith('deleted file mode')) f.status = 'deleted'
    else if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('index ') || line.startsWith('similarity') || line.startsWith('rename ')) continue
    else if (line.startsWith('@@') || line.startsWith('+') || line.startsWith('-') || line.startsWith(' ')) {
      f.lines.push(line)
      if (line.startsWith('+')) f.adds++
      else if (line.startsWith('-')) f.dels++
    }
  }
  return files
}

const TONE: Record<FileDiff['status'], string> = { added: 'text-ok', deleted: 'text-err', renamed: 'text-info', changed: 'text-warn' }
const MARK: Record<FileDiff['status'], string> = { added: 'A', deleted: 'D', renamed: 'R', changed: 'M' }

function Bar({ adds, dels }: { adds: number; dels: number }) {
  const total = adds + dels || 1
  const n = 5
  const a = Math.round((adds / total) * n)
  return (
    <span className="flex gap-px" aria-hidden>
      {Array.from({ length: n }, (_, i) => <span key={i} className={cn('h-2 w-2 rounded-[1px]', i < a ? 'bg-ok' : i < n && dels ? 'bg-err' : 'bg-surface-4')} />)}
    </span>
  )
}

export function Changes({ workspaceId, prUrl }: { workspaceId: string; prUrl?: string }) {
  const diff = useQuery({
    queryKey: ['ws', workspaceId, 'diff'],
    queryFn: ({ signal }) => get<Diff>(`/api/workspaces/${enc(workspaceId)}/diff`, signal),
    staleTime: 60_000,
    retry: false,
  })
  const [open, setOpen] = useState<Record<string, boolean>>({})
  if (diff.error) {
    const gone = diff.error instanceof ApiError && diff.error.status === 404
    return gone ? null : <p className="text-xs text-fg-faint">The changes could not be read from GitHub just now.</p>
  }
  if (!diff.data) return <div className="h-16 animate-pulse rounded-md bg-surface-2" />
  const files = parseDiff(diff.data.patch)
  if (!files.length) return null
  const adds = files.reduce((n, f) => n + f.adds, 0)
  const dels = files.reduce((n, f) => n + f.dels, 0)
  return (
    <section>
      <div className="mb-2 flex items-center gap-2 text-xs">
        <h3 className="font-semibold text-fg-subtle">Files changed</h3>
        <span className="text-fg-faint">{files.length} {files.length === 1 ? 'file' : 'files'} · <span className="text-ok">+{adds}</span> <span className="text-err">−{dels}</span> against {diff.data.base}</span>
        <span className="flex-1" />
        {prUrl && <a href={`${prUrl}/files`} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-accent hover:underline">Review on GitHub <ExternalLink size={11} /></a>}
      </div>
      <ul className="overflow-hidden rounded-md border border-border">
        {files.map((f) => (
          <li key={f.path} className="border-b border-border last:border-b-0">
            <button onClick={() => setOpen((o) => ({ ...o, [f.path]: !o[f.path] }))} type="button" aria-expanded={!!open[f.path]} className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 hover:bg-hover">
              {open[f.path] ? <ChevronDown size={12} className="shrink-0 text-fg-faint" /> : <ChevronRight size={12} className="shrink-0 text-fg-faint" />}
              <span className={cn('w-3 shrink-0 font-mono text-2xs font-semibold', TONE[f.status])} title={f.status}>{MARK[f.status]}</span>
              <span className="min-w-0 flex-1 truncate font-mono text-[11px]" title={f.from ? `${f.from} → ${f.path}` : f.path}>
                <span className="text-fg-faint">{f.path.includes('/') ? f.path.slice(0, f.path.lastIndexOf('/') + 1) : ''}</span>{f.path.split('/').pop()}
              </span>
              <span className="shrink-0 tabular-nums text-2xs"><span className="text-ok">+{f.adds}</span> <span className="text-err">−{f.dels}</span></span>
              <Bar adds={f.adds} dels={f.dels} />
            </button>
            {open[f.path] && (
              <pre className="max-h-96 overflow-auto border-t border-border bg-bg py-1 font-mono text-[11px] leading-[1.55]">
                {f.lines.length === 0 ? <span className="px-3 text-fg-faint">No text to show (a binary file, or only renamed).</span> : f.lines.map((l, i) => (
                  <div key={i} className={cn('px-3', l.startsWith('@@') ? 'text-info/80' : l.startsWith('+') ? 'bg-diff-add text-fg' : l.startsWith('-') ? 'bg-diff-del text-fg' : 'text-fg-muted')}>{l || ' '}</div>
                ))}
              </pre>
            )}
          </li>
        ))}
      </ul>
      {diff.data.truncated && <p className="mt-1 text-2xs text-fg-faint">Long diff: only the start is shown. The rest is on GitHub.</p>}
    </section>
  )
}
